import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  changeIntegrationLegalHold,
  changeIntegrationPreventLanguages,
  changeIntegrationPreventTranslations,
  changeIntegrationRetentionPeriod,
  changeIntegrationRetentionPolicy,
  changeIntegrationTranslations,
  changeIntegrationUseCases,
  fetchIntegrationSettingsFailure,
  fetchIntegrationSettingsRequest,
  fetchIntegrationSettingsSuccess,
  resetForm,
  upsertIntegrationSettingsFailure,
  upsertIntegrationSettingsRequest,
  upsertIntegrationSettingSuccess,
} from 'actions/integrationSettings';

import { CustomerIntegrationSettings, ErrorObject } from 'types';

type IntegrationSettingsState = {
  integrationSettings: CustomerIntegrationSettings | null;
  integrationSettingsForm: Partial<CustomerIntegrationSettings> & { retention_period: string };
  loading: string[];
  error: ErrorObject | null;
};

const defaultState: IntegrationSettingsState = {
  integrationSettings: null,
  integrationSettingsForm: {
    legal_hold: undefined,
    email_chat_retention_policy: '',
    retention_period: '',
    use_case_uuids: [],
    translations_needed: undefined,
    additional_language_offerings: undefined,
    languages_for_better_prevent: '',
  },
  loading: [],
  error: null,
};

type IntegrationSettingsReducer<P = void> = (
  state: IntegrationSettingsState,
  payload: PayloadAction<P>
) => IntegrationSettingsState | void;

const handleChangeIntegrationLegalHold: IntegrationSettingsReducer<boolean> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = { ...state.integrationSettingsForm, legal_hold: payload };
};

const handleChangeIntegrationRetentionPolicy: IntegrationSettingsReducer<string> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = {
    ...state.integrationSettingsForm,
    email_chat_retention_policy: payload,
  };
};

const handleChangeIntegrationRetentionPeriod: IntegrationSettingsReducer<string> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = {
    ...state.integrationSettingsForm,
    retention_period: payload,
  };
};

const handleChangeIntegrationUseCases: IntegrationSettingsReducer<string[]> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = { ...state.integrationSettingsForm, use_case_uuids: payload };
};

const handleChangeIntegrationTranslations: IntegrationSettingsReducer<boolean> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = {
    ...state.integrationSettingsForm,
    translations_needed: payload,
  };
};

const handleChangeIntegrationPreventTranslations: IntegrationSettingsReducer<boolean> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = {
    ...state.integrationSettingsForm,
    additional_language_offerings: payload,
  };
};

const handleChangeIntegrationPreventLanguages: IntegrationSettingsReducer<string> = (
  state,
  { payload }
) => {
  state.integrationSettingsForm = {
    ...state.integrationSettingsForm,
    languages_for_better_prevent: payload,
  };
};

const handleResetForm: IntegrationSettingsReducer = (state) => {
  if (state.integrationSettings) {
    state.integrationSettingsForm = { ...state.integrationSettings, retention_period: '0' };
  } else {
    state.integrationSettingsForm = defaultState.integrationSettingsForm;
  }
};

const handleFetchIntegrationSettingsRequest: IntegrationSettingsReducer = (state) => {
  state.loading = [...state.loading, fetchIntegrationSettingsRequest.toString()];
};

const handleFetchIntegrationSettingsFailure: IntegrationSettingsReducer<ErrorObject> = (
  state,
  { payload }
) => {
  state.loading = state.loading.filter((l) => l !== fetchIntegrationSettingsRequest.toString());
  state.error = payload;
};

const handleFetchIntegrationSettingsSuccess: IntegrationSettingsReducer<
  CustomerIntegrationSettings
> = (state, { payload }) => {
  state.loading = state.loading.filter((l) => l !== fetchIntegrationSettingsRequest.toString());
  if (payload) {
    state.integrationSettings = payload;
    state.integrationSettingsForm = {
      ...payload,
      retention_period: state.integrationSettingsForm.retention_period || '0',
    };
  } else {
    state.integrationSettings = null;
    state.integrationSettingsForm = defaultState.integrationSettingsForm;
  }
};

const handleUpsertIntegrationSettingsRequest: IntegrationSettingsReducer = (state) => {
  state.loading = [...state.loading, upsertIntegrationSettingsRequest.toString()];
};

const handleUpsertIntegrationSettingsFailure: IntegrationSettingsReducer<ErrorObject> = (
  state,
  { payload }
) => {
  state.loading = state.loading.filter((l) => l !== upsertIntegrationSettingsRequest.toString());
  state.error = payload;
};

const handleUpsertIntegrationSettingsSuccess: IntegrationSettingsReducer<
  CustomerIntegrationSettings
> = (state, { payload }) => {
  state.loading = state.loading.filter((l) => l !== upsertIntegrationSettingsRequest.toString());
  state.integrationSettings = payload;
  state.integrationSettingsForm = {
    ...payload,
    retention_period: state.integrationSettingsForm.retention_period,
  };
};

const handlers = {
  [changeIntegrationLegalHold.toString()]: handleChangeIntegrationLegalHold,
  [changeIntegrationRetentionPolicy.toString()]: handleChangeIntegrationRetentionPolicy,
  [changeIntegrationRetentionPeriod.toString()]: handleChangeIntegrationRetentionPeriod,
  [changeIntegrationUseCases.toString()]: handleChangeIntegrationUseCases,
  [changeIntegrationTranslations.toString()]: handleChangeIntegrationTranslations,
  [changeIntegrationPreventTranslations.toString()]: handleChangeIntegrationPreventTranslations,
  [changeIntegrationPreventLanguages.toString()]: handleChangeIntegrationPreventLanguages,
  [resetForm.toString()]: handleResetForm,

  [fetchIntegrationSettingsRequest.toString()]: handleFetchIntegrationSettingsRequest,
  [fetchIntegrationSettingsFailure.toString()]: handleFetchIntegrationSettingsFailure,
  [fetchIntegrationSettingsSuccess.toString()]: handleFetchIntegrationSettingsSuccess,

  [upsertIntegrationSettingsRequest.toString()]: handleUpsertIntegrationSettingsRequest,
  [upsertIntegrationSettingsFailure.toString()]: handleUpsertIntegrationSettingsFailure,
  [upsertIntegrationSettingSuccess.toString()]: handleUpsertIntegrationSettingsSuccess,
};

const integrationSettingsReducer = createReducer(defaultState, handlers);

export default integrationSettingsReducer;
